import {formatLocalDateString} from "../../../common/util/date";
import {
    AuthorizationTypeMap,
    ClientReportReportStatusMap,
    OfferedHoursStatusMapWithDefault,
    ParentCaseServiceTypeNameToValue,
    ProcedureCodeWithDefaultValue,
} from "../domain";
import {SubmittedRequestTypeMap, SubmittedStatusMap} from "../submitted-request/SubmittedRequestDomain";
import {ExportAuditModule} from "./domain/types";

/**
 * Renders an export's stored filter snapshot for the audit page: the applied filters as "Label: value"
 * lines, and the search text on its own - two audit fields, two columns.
 *
 * <p>Values read as the grids' filter chips read them - stored `ABA_Treatment_Authorization` shows as
 * `ABA Treatment Authorization` - which is why this lives beside the chips' enum maps rather than on
 * the backend. Labels come from each grid's filter panel instead: chip wording differs per module and
 * most chips carry no label at all.
 */

const SEARCH_KEY = 'search';
/** Grid state, not filter criteria. */
const SORT_KEYS = ['sortBy', 'sortDir'];

type FilterSpec = {
    /** Ordered: lines follow the grid's filter-panel order. */
    labels: [string, string][];
    dateKeys: string[];
    /** The chips' display maps, tried in order - mirrors each badge's `displayValue`. */
    valueMaps: Map<string, string>[];
};

const SPEC_BY_MODULE: Partial<Record<ExportAuditModule, FilterSpec>> = {
    AUTHORIZATIONS: {
        labels: [
            // recordType and faRecordType are two nullable projections of the same authType field,
            // so at most one is ever set.
            ['recordType', 'Type'],
            ['faRecordType', 'Type'],
            ['serviceTypes', 'Service Type'],
            ['status', 'Status'],
            ['reportStatus', 'Report Status'],
            ['reportSubmittedDateFrom', 'Report Submitted Date From'],
            ['reportSubmittedDateTo', 'Report Submitted Date To'],
            ['reportDueDateFrom', 'Report Due Date From'],
            ['reportDueDateTo', 'Report Due Date To'],
        ],
        dateKeys: ['reportSubmittedDateFrom', 'reportSubmittedDateTo', 'reportDueDateFrom', 'reportDueDateTo'],
        valueMaps: [ParentCaseServiceTypeNameToValue, AuthorizationTypeMap, ClientReportReportStatusMap],
    },
    PARENT_CASES: {
        labels: [
            ['cpaStatus', 'Status'],
            ['serviceTypes', 'Service Type'],
            ['cities', 'City'],
            ['referralAcceptedFrom', 'Placement Date From'],
            ['referralAcceptedTo', 'Placement Date To'],
        ],
        dateKeys: ['referralAcceptedFrom', 'referralAcceptedTo'],
        valueMaps: [ParentCaseServiceTypeNameToValue],
    },
    SUBMITTED_REQUESTS: {
        labels: [
            ['requestType', 'Request Type'],
            ['statuses', 'Status'],
            ['submittedBy', 'Submitted By'],
            ['submittedDateFrom', 'Submitted Date From'],
            ['submittedDateTo', 'Submitted Date To'],
            ['closedDateFrom', 'Closed Date From'],
            ['closedDateTo', 'Closed Date To'],
        ],
        dateKeys: ['submittedDateFrom', 'submittedDateTo', 'closedDateFrom', 'closedDateTo'],
        valueMaps: [SubmittedRequestTypeMap, SubmittedStatusMap],
    },
    AT_RISK: {
        labels: [
            ['cities', 'City'],
            ['fromResponseDueDate', 'Response Due Date From'],
            ['toResponseDueDate', 'Response Due Date To'],
        ],
        dateKeys: ['fromResponseDueDate', 'toResponseDueDate'],
        // Cities and free text only - the grid filters on nothing enum-backed.
        valueMaps: [],
    },
    AUTHORIZATION_BILLING: {
        labels: [
            ['offeredHoursStatus', 'Offered Hours Status'],
            ['procedureCode', 'Procedure Code'],
        ],
        dateKeys: [],
        // authorizationGuid is scoping context, not a user-picked filter, so it's left off labels.
        valueMaps: [OfferedHoursStatusMapWithDefault, ProcedureCodeWithDefaultValue],
    },
};

/** A date that will not parse is still an audit value - keep it as stored. */
const toDisplayDate = (raw: string) => formatLocalDateString(raw) || raw;

const toDisplayValue = (raw: string, valueMaps: Map<string, string>[]) => {
    for (const map of valueMaps) {
        const mapped = map.get(raw);
        if (mapped) {
            return mapped;
        }
    }
    return raw;
};

/** An unapplied filter is not an audit fact: null, blank and empty collections render nothing. */
const render = (value: unknown, isDate: boolean, valueMaps: Map<string, string>[]): string | null => {
    if (value === null || value === undefined) {
        return null;
    }
    if (Array.isArray(value)) {
        const joined = value
            .map((entry) => `${entry}`.trim())
            .filter((entry) => entry !== '')
            .map((entry) => toDisplayValue(entry, valueMaps))
            .join(', ');
        return joined === '' ? null : joined;
    }
    const text = `${value}`.trim();
    if (text === '') {
        return null;
    }
    return isDate ? toDisplayDate(text) : toDisplayValue(text, valueMaps);
};

/** A module without a spec degrades to raw keys rather than blank. */
const describeUnmapped = (filters: Record<string, unknown>) =>
    Object.keys(filters)
        .filter((key) => !SORT_KEYS.includes(key) && key !== SEARCH_KEY)
        .map((key) => {
            const value = render(filters[key], false, []);
            return value === null ? null : `${key}: ${value}`;
        })
        .filter((line): line is string => line !== null);

const describeMapped = (spec: FilterSpec, filters: Record<string, unknown>) =>
    spec.labels
        .map(([key, label]) => {
            const value = render(filters[key], spec.dateKeys.includes(key), spec.valueMaps);
            return value === null ? null : `${label}: ${value}`;
        })
        .filter((line): line is string => line !== null);

/** A malformed or absent snapshot must not take down the page. */
const parseFilters = (filtersJson: string | null): Record<string, unknown> | null => {
    if (!filtersJson) {
        return null;
    }
    try {
        const filters = JSON.parse(filtersJson);
        return filters && typeof filters === 'object' ? filters : null;
    } catch {
        return null;
    }
};

export const describeFilters = (module: ExportAuditModule, filtersJson: string | null): string[] => {
    const filters = parseFilters(filtersJson);
    if (filters === null) {
        return [];
    }
    const spec = SPEC_BY_MODULE[module];
    return spec ? describeMapped(spec, filters) : describeUnmapped(filters);
};

/** The search text as the exporter typed it - its own audit field, so no label of its own. */
export const describeSearch = (filtersJson: string | null): string | null => {
    const filters = parseFilters(filtersJson);
    return filters === null ? null : render(filters[SEARCH_KEY], false, []);
};
